// ============================================================
// brand.ts — Marca configurable (nombre, logo, colores)
// Los valores viven en site-config.json → "branding" y se editan
// desde Admin → Configuración del sitio → Marca.
// Se aplican en tiempo de ejecución: la misma imagen Docker sirve
// para todas las tiendas.
// ============================================================

export interface BrandColors {
  primary:       string   // color de acento: botones, cintilla, enlaces
  primaryHover?: string   // opcional: si va vacío se calcula oscureciendo el primario
  secondary:     string   // color secundario / destacado (antes "gold")
  footerText?:   string   // opcional: enlaces, dirección e íconos del pie de página
  mutedText?:    string   // opcional: texto secundario (subtítulos, descripciones)
}

export interface Branding {
  name:              string
  logoText:          [string, string]   // "URBAN" + "WEAR" (la 2ª parte va en color primario)
  logoImage?:        string             // logo en imagen (panel admin, header opcional)
  heroImage?:        string             // imagen grande del hero de la portada
  favicon?:          string
  useImageInHeader?: boolean
  colors:            BrandColors
  seo?:              { title?: string; description?: string }
  location?:         string             // "Monterrey, México" (pie de página)
  footerText?:       string             // descripción corta bajo el logo del pie
}

export const DEFAULT_BRANDING: Branding = {
  name:      'Mi Tienda',
  logoText:  ['MI', 'TIENDA'],
  logoImage: '',
  heroImage: '',
  favicon:   '',
  useImageInHeader: false,
  colors:    { primary: '#cc0000', primaryHover: '', secondary: '#c9a84c', footerText: '', mutedText: '' },
  seo:       { title: '', description: '' },
  location:  '',
  footerText: '',
}

/** Combina lo guardado con los valores por defecto (tolera configs viejas sin "branding"). */
export function resolveBranding(raw?: Partial<Branding> | null): Branding {
  const b: Partial<Branding> = raw ?? {}
  const logoText = Array.isArray(b.logoText) && b.logoText.length
    ? [b.logoText[0] ?? '', b.logoText[1] ?? ''] as [string, string]
    : (b.name ? [b.name.toUpperCase(), ''] as [string, string] : DEFAULT_BRANDING.logoText)
  return {
    ...DEFAULT_BRANDING,
    ...b,
    logoText,
    colors: { ...DEFAULT_BRANDING.colors, ...(b.colors ?? {}) },
    seo:    { ...DEFAULT_BRANDING.seo,    ...(b.seo ?? {}) },
  }
}

// ---- Utilidades de color ----
const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

export function isValidHex(hex?: string): boolean {
  return !!hex && HEX_RE.test(hex.trim())
}

export function normalizeHex(hex: string): string {
  let h = hex.trim().replace('#', '')
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  return '#' + h.toLowerCase()
}

/** "#cc0000" → "204, 0, 0" (para usar en rgba(var(--accent-rgb), .15)) */
export function hexToRgb(hex: string): string {
  const h = normalizeHex(hex).slice(1)
  const n = parseInt(h, 16)
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
}

/** Oscurece un color en un porcentaje (0–1). */
export function darken(hex: string, amount = 0.17): string {
  const [r, g, b] = hexToRgb(hex).split(',').map(v => parseInt(v, 10))
  const f = (c: number) => Math.max(0, Math.min(255, Math.round(c * (1 - amount))))
  return '#' + [f(r), f(g), f(b)].map(c => c.toString(16).padStart(2, '0')).join('')
}

/** Negro o blanco según qué se lea mejor sobre el color dado (luminancia relativa WCAG). */
export function contrastText(hex: string): string {
  const [r, g, bl] = hexToRgb(hex).split(',').map(v => parseInt(v, 10) / 255)
  const lin = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(bl)
  return L > 0.4 ? '#0a0a0a' : '#ffffff'
}

/** Nombre apto para archivos: "Urban Wear" → "urban-wear" */
export function brandSlug(name: string): string {
  return (name || 'tienda')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'tienda'
}

// ---- Aplicar al documento ----
function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel='${rel}']`)
  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    document.head.appendChild(el)
  }
  el.removeAttribute('type')   // el favicon puede ser png/jpg/svg
  el.href = href
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}='${key}']`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

// Última marca aplicada (para código fuera de componentes, p. ej. nombres de archivos CSV)
let currentBranding: Branding = DEFAULT_BRANDING
export const getCurrentBranding = () => currentBranding

/** Aplica colores (variables CSS), título, descripción y favicon. */
export function applyBranding(branding: Branding) {
  currentBranding = branding
  const root = document.documentElement.style
  const { primary, primaryHover, secondary } = branding.colors

  if (isValidHex(primary)) {
    const p = normalizeHex(primary)
    const h = isValidHex(primaryHover) ? normalizeHex(primaryHover!) : darken(p)
    root.setProperty('--accent',     p)
    root.setProperty('--accent-h',   h)
    root.setProperty('--accent-rgb', hexToRgb(p))
    root.setProperty('--on-accent',  contrastText(p))
  }
  if (isValidHex(secondary)) {
    const s = normalizeHex(secondary)
    root.setProperty('--gold',     s)
    root.setProperty('--gold-rgb', hexToRgb(s))
  }

  // Colores de texto opcionales: si se dejan vacíos se usan los valores por defecto del CSS
  const optional: [string, string | undefined][] = [
    ['--footer-text', branding.colors.footerText],
    ['--muted',       branding.colors.mutedText],
  ]
  for (const [prop, val] of optional) {
    if (isValidHex(val)) root.setProperty(prop, normalizeHex(val!))
    else root.removeProperty(prop)
  }

  const title = branding.seo?.title || branding.name
  if (title) {
    document.title = title
    setMeta('property', 'og:title', title)
  }
  if (branding.seo?.description) {
    setMeta('name', 'description', branding.seo.description)
    setMeta('property', 'og:description', branding.seo.description)
  }
  if (branding.favicon) setLink('icon', branding.favicon)
}
